// Direct Cognito calls from the browser — mirrors expo's useCognitoPasswordAuth + useCognitoForgotPassword.
// Used so admin login / forgot password go through the same Cognito user pool as expo.

const COGNITO_REGION = process.env.REACT_APP_COGNITO_REGION || "us-east-1";
const COGNITO_ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;

export class PasswordResetRequiredError extends Error {
  constructor(email) {
    super("Password reset required");
    this.name = "PasswordResetRequiredError";
    this.email = email;
  }
}

export class NewPasswordRequiredError extends Error {
  constructor(email, session) {
    super("New password required");
    this.name = "NewPasswordRequiredError";
    this.email = email;
    this.session = session;
  }
}

async function cognitoRequest(target, body) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const type = data.__type || "";
    const err = new Error(data.message || `${target} failed`);
    err.cognitoType = type.split("#").pop();
    throw err;
  }
  return data;
}

// Returns the Cognito IdToken on success. Throws NewPasswordRequiredError if
// Cognito requires the user to set a new password (admin reset or first-time
// admin-created account) — the caller must complete the challenge via
// cognitoRespondToNewPassword.
export async function cognitoSignIn(email, password) {
  try {
    const data = await cognitoRequest("InitiateAuth", {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: CLIENT_ID,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    });
    if (data?.ChallengeName === "NEW_PASSWORD_REQUIRED") {
      throw new NewPasswordRequiredError(email, data.Session);
    }
    return data?.AuthenticationResult?.IdToken;
  } catch (e) {
    if (e.cognitoType === "PasswordResetRequiredException") {
      throw new PasswordResetRequiredError(email);
    }
    throw e;
  }
}

// Completes the NEW_PASSWORD_REQUIRED challenge, returning the Cognito IdToken.
export async function cognitoRespondToNewPassword(email, session, newPassword) {
  const data = await cognitoRequest("RespondToAuthChallenge", {
    ClientId: CLIENT_ID,
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    Session: session,
    ChallengeResponses: {
      USERNAME: email,
      NEW_PASSWORD: newPassword,
    },
  });
  return data?.AuthenticationResult?.IdToken;
}

export async function cognitoForgotPassword(email) {
  await cognitoRequest("ForgotPassword", {
    ClientId: CLIENT_ID,
    Username: email,
  });
}

export async function cognitoConfirmForgotPassword(email, code, newPassword) {
  await cognitoRequest("ConfirmForgotPassword", {
    ClientId: CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  });
}
